import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import fileDownload from "js-file-download";
import {
  setGDDKFilterStation,
  setGDDKFilterResourceType,
  setGDDKFilterGDDKPeriod,
  getActiveGDDKListRequest,
  setHistoryGDDKFilterResourceType,
  setHistoryGDDKFilterGDDKPeriod,
  getGDDKHistoryListRequest,
} from "../../../redux/actions/gddk";
import { convertAmountToFrontend } from "../../../helpers/convert";
import ComboBoxStation from "../../../components/ComboBoxStation";
import { Input, Select } from "../../../components/Inputs";
import Button from "../../../components/Button";
import EmptyState from "../../../components/EmptyState/EmptyState";
import Loading from "../../../components/Loading";
import DataTable from "../../../components/DataTable";
import Pill from "../../../components/Pill";
import moment from "moment";
import GDDKDynamicModal from "../../../components/Modal/GDDKDynamicModal";
import { setNotification } from "../../../helpers/notification";
import { monthsAndResourceTypes } from "../../../constants/translations";
import { resourceTypes } from "../../../constants/selects";
import { API_EXPORT } from "../../../helpers/api";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";

const ProductionDataGDDKOperations = ({ t }) => {
  const dispatch = useDispatch();
  const organizationId = useSelector(
    (state) => state.authorization.user.organization.organizationId
  );
  const organization = useSelector(
    (state) => state.authorization.user.organization
  );
  const {
    filterStation,
    filterResourceType,
    filterGDDKPeriod,
    activeList,
    activeLoading,
    filterHistoryResourceType,
    filterHistoryGDDKPeriod,
    historyList,
    historyLoading,
  } = useSelector((state) => state.gddk);

  const [showGDDKModal, setShowGDDKModal] = useState(false);
  const [selectedGDDK, setSelectedGDDK] = useState({});
  const [sortedColumn, setSortedColumn] = useState("productionPeriod");
  const [sortDirection, setSortDirection] = useState("desc");
  const [exportLoading, setExportLoading] = useState(false);
  const [activePage, setActivePage] = useState(0);
  const translations = monthsAndResourceTypes(t);

  const selectResourceType = resourceTypes(t);

  const activeGDDKListColumns = [
    {
      name: t("pages.default.certificates.table.organizationName"),
      selector: "organizationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.certificates.table.stationName"),
      selector: "stationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.certificates.table.resourceType"),
      selector: "resourceType",
      sortable: true,
      cell: (row) => {
        return translations[row.resourceType];
      },
    },
    {
      name: t("pages.default.certificates.table.productionPeriod"),
      selector: "productionPeriod",
      sortable: true,
      cell: (row) => {
        const term = moment(row.term);
        return `${translations[term.month() + 1]} ${term.year()}`;
      },
    },
    {
      name: t("pages.default.certificates.table.durationOfUse"),
      selector: "durationOfUse",
      sortable: true,
      cell: (row) => {
        const currentDate = moment();
        const expireDate = moment(row.expireDate);
        return Math.round(
          moment.duration(expireDate.diff(currentDate)).asMonths()
        );
      },
    },
    {
      name: t("pages.default.gddkOperations.table.gddkPeriod"),
      selector: "gddkPeriod",
      sortable: true,
      cell: (row) => {
        const gddTerm = moment(row.gddkTerm);
        return `${translations[gddTerm.month() + 1]} ${gddTerm.year()}`;
      },
    },
    {
      name: t("pages.default.certificates.table.debtDocumentCount"),
      selector: "certifiableAmount",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-red-300">
          {convertAmountToFrontend(row.certifiableAmount)}
        </div>
      ),
    },
    {
      name: t("pages.default.certificates.table.status"),
      selector: "gddkStatus",
      sortable: true,
      width: "180px",
      cell: (row) => (
        <Pill
          label={t("pages.default.gddkOperations.table.notCompleted")}
          type="important"
        />
      ),
    },
    {
      name: t("pages.default.certificates.table.process"),
      selector: "process",
      sortable: false,
      width: "140px",
      cell: (row) => (
        <div style={{ display: "flex" }}>
          <Button
            title={t("ui.default.review")}
            suffixIcon="search"
            type="secondary"
            onClick={() => handleShowGDDKModal(row)}
          />
        </div>
      ),
    },
  ];

  const gddkHistoryListColumns = [
    {
      name: t("pages.default.certificates.table.organizationName"),
      selector: "organizationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.certificates.table.stationName"),
      selector: "stationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.certificates.table.resourceType"),
      selector: "resourceType",
      sortable: true,
      cell: (row) => {
        return translations[row.resourceType];
      },
    },
    {
      name: t("pages.default.certificates.table.productionPeriod"),
      selector: "productionPeriod",
      sortable: true,
      cell: (row) => {
        const term = moment(row.term);
        return `${translations[term.month() + 1]} ${term.year()}`;
      },
    },
    {
      name: t("pages.default.certificates.table.durationOfUse"),
      selector: "durationOfUse",
      sortable: true,
      cell: (row) => {
        const currentDate = moment();
        const expireDate = moment(row.expireDate);
        return Math.round(
          moment.duration(expireDate.diff(currentDate)).asMonths()
        );
      },
    },
    {
      name: t("pages.default.gddkOperations.table.gddkPeriod"),
      selector: "gddkPeriod",
      sortable: true,
      cell: (row) => {
        const gddTerm = moment(row.gddkTerm);
        return `${translations[gddTerm.month() + 1]} ${gddTerm.year()}`;
      },
    },
    {
      name: t("pages.default.certificates.table.gddkDocumentCount"),
      selector: "amount",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-red-300">
          {convertAmountToFrontend(row.amount)}
        </div>
      ),
    },
    {
      name: t("pages.default.gddkOperations.table.bProdcutionAmount"),
      selector: "bProdcutionAmount",
      sortable: true,
      cell: (row) => (
        <div>
          {row.certifiableAmount
            ? convertAmountToFrontend(row.certifiableAmount)
            : "-"}
        </div>
      ),
    },
    {
      name: t("pages.default.certificates.table.status"),
      selector: "gddkStatus",
      sortable: true,
      cell: (row) => (
        <Pill label={t("ui.default.codes.SUCCESSFUL")} type="success" />
      ),
    },
    {
      name: t("pages.default.certificates.table.process"),
      selector: "process",
      sortable: false,
      cell: (row) => (
        <div style={{ display: "flex" }}>
          <Button
            title={t("ui.default.review")}
            suffixIcon="search"
            type="secondary"
            onClick={() => handleShowGDDKModal(row)}
          />
        </div>
      ),
    },
  ];

  useEffect(() => {
    filterActiveGDDKList();
    filterGDDKHistoryList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    console.log(organization);
  }, []);

  const selectStation = (station) => {
    dispatch(setGDDKFilterStation(station));
  };

  const filterChange = (e) => {
    switch (e.target.name) {
      case "gddkPeriod":
        dispatch(setGDDKFilterGDDKPeriod(e.target.value.replace("-", "")));
        break;
      case "resourceType":
        dispatch(setGDDKFilterResourceType(e.target.value));
        break;
      case "historyGDDKPeriod":
        dispatch(
          setHistoryGDDKFilterGDDKPeriod(e.target.value.replace("-", ""))
        );
        break;
      case "historyResourceType":
        dispatch(setHistoryGDDKFilterResourceType(e.target.value));
        break;
      default:
        break;
    }
  };

  const filterActiveGDDKList = () => {
    dispatch(
      getActiveGDDKListRequest({
        organizationId,
        stationId: filterStation ? filterStation.stationId : "",
        resourceType: filterResourceType === "-" ? "" : filterResourceType,
        term: filterGDDKPeriod,
      })
    );
  };

  const filterGDDKHistoryList = () => {
    dispatch(
      getGDDKHistoryListRequest({
        organizationId,
        stationId: filterStation ? filterStation.stationId : "",
        resourceType:
          filterHistoryResourceType === "-" ? "" : filterHistoryResourceType,
        term: filterHistoryGDDKPeriod,
      })
    );
  };

  const downloadCommitmentStatus = async () => {
    setExportLoading(true);
    try {
      const res = await API_EXPORT("/production-data/export/excel", {
        organizationId: organization ? organization?.organizationId : null,
        stationId: filterStation ? filterStation?.stationId : null,
        resourceType:
          filterHistoryResourceType === "-" ? "" : filterHistoryResourceType,
        term: filterHistoryGDDKPeriod ? filterHistoryGDDKPeriod : null,
        lang: window.localStorage.i18nextLng,
      });
      const [, filename] =
        res?.headers["content-disposition"].split("filename=");
      fileDownload(res.data, filename);
      setExportLoading(false);
      setNotification("success", t("ui.default.downloaded"));
    } catch (err) {
      err.response && setNotification("error", err.response.data.message);
      setExportLoading(false);
    }
  };

  const handleShowGDDKModal = (row) => {
    setSelectedGDDK(row);
    setShowGDDKModal(true);
  };
  const handleClick = (activePage, selector, direction) => {
    console.log("page", activePage, selector, direction);
    setActivePage(activePage);
    dispatch(
      getGDDKHistoryListRequest({
        organizationId,
        stationId: filterStation ? filterStation.stationId : "",
        resourceType:
          filterHistoryResourceType === "-" ? "" : filterHistoryResourceType,
        term: filterHistoryGDDKPeriod,
        page: activePage,
        selector: selector,
        direction: direction,
      })
    );
  };
  return (
    <>
      <div className="km__p--card">
        <div className="km__p--gddk-header">
          <div className="km__u--text_title">
            {t("pages.default.gddkOperations.activeGDDKTitle")}
          </div>
        </div>
        <div className="km__p--gddk-filter-container">
          <ComboBoxStation
            t={t}
            selectedStation={filterStation}
            selectStation={selectStation}
          />
          <Select
            options={selectResourceType}
            name="resourceType"
            value={filterResourceType}
            onChange={filterChange}
          />
          <Input
            type="month"
            placeholder={t(
              "pages.default.gddkOperations.filters.allGDDKPeriods"
            )}
            name="gddkPeriod"
            value={filterGDDKPeriod}
            onChange={filterChange}
          />
          <Button
            label={t("ui.default.filter")}
            onClick={filterActiveGDDKList}
          />
        </div>

        {!activeLoading && (
          <DataTable
            columns={activeGDDKListColumns}
            data={activeList}
            rowKey="id"
            pagination
            paginationPerPage={10}
          />
        )}
        {activeList.length === 0 && !activeLoading && (
          <EmptyState
            icon="file-text"
            text={t("pages.default.gddkOperations.activeNotFound")}
          />
        )}
        {activeLoading && (
          <Loading text={t("pages.default.gddkOperations.activeLoading")} />
        )}
      </div>

      <div className="km__p--card">
        <div className="km__p--gddk-header">
          <div className="km__u--text_title">
            {t("pages.default.gddkOperations.historyGDDKTitle")}
          </div>
        </div>
        <div className="km__p--gddk-filter-container">
          <ComboBoxStation
            t={t}
            selectedStation={filterStation}
            selectStation={selectStation}
          />
          <Select
            options={selectResourceType}
            name="historyResourceType"
            value={filterHistoryResourceType}
            onChange={filterChange}
          />
          <Input
            type="month"
            placeholder={t(
              "pages.default.gddkOperations.filters.allGDDKPeriods"
            )}
            name="historyGDDKPeriod"
            value={filterHistoryGDDKPeriod}
            onChange={filterChange}
          />
          <Button
            label={t("ui.default.filter")}
            onClick={filterGDDKHistoryList}
          />
          {organization?.licenseType === "PRODUCTION" && (
            <Button
              type="secondary"
              label={t("ui.default.download")}
              suffixIcon="download"
              onClick={downloadCommitmentStatus}
              disabled={exportLoading}
              id="c-download"
            />
          )}
        </div>

        {!historyLoading && (
          // <DataTable
          //   columns={gddkHistoryListColumns}
          //   data={historyList}
          //   rowKey='id'
          //   pagination
          //   paginationPerPage={10}
          // />
          <DataTableServerSideRendering
            columns={gddkHistoryListColumns}
            data={historyList?.dataList}
            rowKey="id"
            sortedColumn={sortedColumn}
            setSortedColumn={setSortedColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            pagination
            activePage={activePage}
            totalPageCount={historyList?.total?.totalPageCount}
            handleFetchData={handleClick}
          />
        )}
        {historyList.length === 0 && !historyLoading && (
          <EmptyState
            icon="file-text"
            text={t("pages.default.gddkOperations.historyNotFound")}
          />
        )}
        {historyLoading && (
          <Loading text={t("pages.default.gddkOperations.historyLoading")} />
        )}
      </div>

      <GDDKDynamicModal
        t={t}
        isOpen={showGDDKModal}
        toggle={() => setShowGDDKModal(false)}
        selectedGDDK={selectedGDDK}
      />
    </>
  );
};

export default ProductionDataGDDKOperations;
